// Workbench Replacement Options badge — derives the per-row icon state from
// the server's nblock-only payload + the current ingredient's batch supply
// flags. The icon is a thin renderer; this hook is where the predicate
// logic lives.
//
// Stale-while-recomputing: the previous derived state is cached per
// (formulaId, compoundId), so editing a row (which causes perItem to flip
// to undefined for ~5–15 s while a fresh nblock_only job runs) keeps the
// previously-shown dots in place instead of flashing back to DEFAULT.
//
// Cache eviction is driven by the consumer (FlaskPanel) — it calls
// evictReplacementBadge in the effect that watches for rows leaving the
// formulation. We don't evict on flavor change because WorkbenchPage's
// outer key={id} remount drops the consumer entirely; the cache entries
// for the previous flavor become unreachable noise but don't affect
// correctness (formulaId is part of the cache key).

import { useEffect, useMemo } from "react";
import type {
  PerItemReplacements,
  ReplacementSet,
} from "@/services/workbench_api";
import type { Target } from "@/utils/types/flavor";
import type { SubstanceSupplyFlags } from "@/utils/types/substance";

export type ReplacementBadgeState =
  | { kind: "disabled" }
  | { kind: "default" }
  | { kind: "better-loss" }
  | { kind: "difficulties" }
  | { kind: "better-loss-and-difficulties" };

type CacheKey = string;
const cacheKey = (formulaId: number | null, compoundId: string): CacheKey =>
  `${formulaId ?? "null"}:${compoundId}`;

const cache = new Map<CacheKey, ReplacementBadgeState>();

/** Drop the cached badge for a row. Called from FlaskPanel when a compound
 *  leaves the formulation (otherwise its stale dots would linger if the
 *  user re-adds the same compound later within the same session). */
export function evictReplacementBadge(
  formulaId: number | null,
  compoundId: string,
): void {
  cache.delete(cacheKey(formulaId, compoundId));
}

function anyBetterLoss(perItem: PerItemReplacements): boolean {
  // Drop improves loss? Compare against per-item base.
  if (
    perItem.baseLoss != null &&
    perItem.dropOnlyLoss != null &&
    perItem.dropOnlyLoss < perItem.baseLoss
  ) {
    return true;
  }
  for (const set of perItem.singles) {
    if (
      perItem.baseLoss != null &&
      set.replacedLoss != null &&
      set.replacedLoss < perItem.baseLoss
    ) {
      return true;
    }
  }
  for (const set of perItem.multis) {
    if (
      perItem.baseLoss != null &&
      set.replacedLoss != null &&
      set.replacedLoss < perItem.baseLoss
    ) {
      return true;
    }
  }
  return false;
}

function anyViable(
  sets: ReplacementSet[],
  predicate: (set: ReplacementSet) => boolean,
): boolean {
  for (const set of sets) {
    if (predicate(set)) return true;
  }
  return false;
}

function deriveDifficulties(
  perItem: PerItemReplacements,
  flags: SubstanceSupplyFlags,
): boolean {
  const swapSets = [...perItem.singles, ...perItem.multis];
  const noSupplier =
    flags.marketCheapest == null &&
    anyViable(swapSets, (s) => s.hasSupplier === true);
  const danger =
    flags.safetySummary?.signal === "Danger" &&
    anyViable(swapSets, (s) => s.safetySignal !== "Danger");
  return noSupplier || danger;
}

export type UseReplacementBadgeArgs = {
  compoundId: string;
  target: Target | null | undefined;
  perItem: PerItemReplacements | undefined;
  flags: SubstanceSupplyFlags | null;
  formulaId: number | null;
};

export function useReplacementBadge(
  args: UseReplacementBadgeArgs,
): ReplacementBadgeState {
  const { compoundId, target, perItem, flags, formulaId } = args;

  const state = useMemo<ReplacementBadgeState>(() => {
    if (target == null) {
      return { kind: "disabled" };
    }
    if (perItem !== undefined && flags !== null) {
      const betterLoss = anyBetterLoss(perItem);
      const difficulties = deriveDifficulties(perItem, flags);
      const kind: ReplacementBadgeState["kind"] =
        betterLoss && difficulties
          ? "better-loss-and-difficulties"
          : betterLoss
            ? "better-loss"
            : difficulties
              ? "difficulties"
              : "default";
      return { kind };
    }
    // Stale-while-recomputing: keep last known state if any.
    const cached = cache.get(cacheKey(formulaId, compoundId));
    return cached ?? { kind: "default" };
  }, [target, perItem, flags, formulaId, compoundId]);

  // Cache the freshly-computed state. Skip caching for:
  //   - "disabled" (driven by target absence — re-derive on the fly when
  //     a target lands rather than carrying a stale "disabled" forward);
  //   - the stale-default fallback we just read from the cache (no new
  //     information).
  useEffect(() => {
    if (state.kind === "disabled") return;
    if (perItem === undefined || flags === null) return;
    cache.set(cacheKey(formulaId, compoundId), state);
  }, [state, perItem, flags, formulaId, compoundId]);

  return state;
}
